/**
 * Polling agents at this polling unit — which parties had an agent PRESENT.
 *
 * Column four of the EC8A is headed NAME/SIGNATURE OF POLLING AGENT. On a typical sheet two
 * or three of twenty rows carry anything; the rest are blank. The agent transcribes that
 * column next to the votes, and this module turns their answers into api.core's
 * `PUT /api/v1/AgentCoverage` body.
 *
 * Naming: the API is called `AgentCoverage`, but "coverage" already means ASSIGNMENT in this
 * app (`child-agent/`, quick-analytics). Presence is not assignment — an assigned agent who
 * never turned up is exactly the finding this record exists for — so in code this is
 * `agent-presence` and on screen it is "Polling agents". The API's name stops at the client.
 *
 * Everything here is pure, so the rules that matter are pinned by tests rather than by
 * clicking. Port of admin-frontend `src/app/result-verification/agent-presence.ts`, adapted
 * for a field agent who must never erase answers they did not touch.
 */

/**
 * Three states, not two.
 *
 * `unrecorded` is not a softer word for `absent`. "Nobody was watching this party's count" is
 * a finding; "I haven't got to that row yet" is a blank. The API represents the second as the
 * ABSENCE of a row, so a half-finished transcription must never silently assert eighteen
 * negatives.
 */
export type PresenceAnswer = 'present' | 'absent' | 'unrecorded';

/**
 * One party's answer, as held in the upload-flow state (and therefore in the draft).
 *
 * A party MISSING from the answers array means "this agent has no opinion" — whatever the
 * server holds for it is carried through untouched. A party present with `'unrecorded'` means
 * the agent explicitly CLEARED an answer, which is the only way this app deletes a stored row.
 */
export interface IPresenceAnswer {
	partyId: number;
	answer: PresenceAnswer;
	/** Form text, so `''` rather than `null`. Only meaningful when `present`. */
	agentName: string;
	note: string;
	/**
	 * Filled in by the app (copied from the stored record, or the own-party preset) and not
	 * yet touched by the agent. A seeded answer is a DEFAULT, not an assertion: at save time
	 * the fresh re-read wins over it, so a verifier's newer answer is never overwritten by a
	 * value the agent merely saw on screen. Any edit through `setAnswer` drops the flag.
	 *
	 *  - `'stored'` — copied from a read. NEVER written: the fresh read is authoritative,
	 *    including when the row has since been DELETED (writing it would resurrect it).
	 *  - `'preset'` — the own-party default. Written only where nothing is stored now.
	 */
	seeded?: 'stored' | 'preset';
}

/* ───────────────────────────── wire shapes ───────────────────────────── */

/** One stored row of `AgentCoverage`. `partyId` is a real FK to `Parties(Id)`. */
export interface IAgentPresenceEntry {
	partyId: number;
	shortName?: string | null;
	longName?: string | null;
	isPresent: boolean;
	agentName: string | null;
	note: string | null;
	recordedAtUtc?: string;
	recordedByUserId?: number | null;
}

/** `data` of both `GET by-polling-unit` and `PUT`. */
export interface IAgentPresenceRoster {
	electionId: number;
	pollingUnitCode: string;
	pollingUnitName?: string | null;
	/** Only parties that HAVE a record. A party absent here is unanswered, not absent. */
	parties: IAgentPresenceEntry[];
	changed?: { created: number; updated: number; deleted: number; unchanged: number } | null;
	warnings?: string[];
}

export interface IAgentPresenceInput {
	partyId: number;
	isPresent: boolean;
	agentName: string | null;
	note: string | null;
}

/**
 * `PUT /api/v1/AgentCoverage` — a FULL REPLACE of one (election, polling unit). A stored party
 * omitted from `parties` is DELETED.
 */
export interface ISetAgentPresenceRequest {
	electionId: number;
	pollingUnitCode: string;
	parties: IAgentPresenceInput[];
}

/** Server-enforced maxima. Clamping client-side makes those errors unreachable. */
export const AGENT_NAME_MAX = 128;
export const AGENT_NOTE_MAX = 500;

/* ───────────────────────────── answers ───────────────────────────── */

/** Trim, collapse whitespace, and treat the backend's literal `"null"` as absent. */
export const cleanText = (value: unknown): string => {
	if (typeof value !== 'string') return '';
	const trimmed = value.trim().replace(/\s+/g, ' ');
	const lower = trimmed.toLowerCase();
	if (lower === 'null' || lower === 'undefined') return '';
	return trimmed;
};

/** Form text → wire value. Blank is `null`, never `''`. */
const toWireText = (value: unknown, max: number): string | null => {
	const cleaned = cleanText(value).slice(0, max);
	return cleaned === '' ? null : cleaned;
};

export const answerOf = (answers: readonly IPresenceAnswer[], partyId: number): PresenceAnswer =>
	answers.find((a) => a.partyId === partyId)?.answer ?? 'unrecorded';

export const findAnswer = (answers: readonly IPresenceAnswer[], partyId: number): IPresenceAnswer | undefined =>
	answers.find((a) => a.partyId === partyId);

/** Upsert one party's answer. Returns a NEW array (state is compared by reference). */
export const setAnswer = (
	answers: readonly IPresenceAnswer[],
	partyId: number,
	patch: Partial<Omit<IPresenceAnswer, 'partyId'>>
): IPresenceAnswer[] => {
	const existing = findAnswer(answers, partyId);
	const next: IPresenceAnswer = {
		partyId,
		answer: existing?.answer ?? 'unrecorded',
		agentName: existing?.agentName ?? '',
		note: existing?.note ?? '',
		...patch,
	};
	// Touched by the agent → an assertion now, not a default.
	delete next.seeded;
	if (patch.agentName !== undefined) next.agentName = patch.agentName.slice(0, AGENT_NAME_MAX);
	if (patch.note !== undefined) next.note = patch.note.slice(0, AGENT_NOTE_MAX);
	return existing ? answers.map((a) => (a.partyId === partyId ? next : a)) : [...answers, next];
};

/**
 * What tapping a pill does. Tapping the active pill clears back to `unrecorded` — the
 * only way to undo a mis-tap without a third button competing for space on a phone.
 */
export const toggleAnswer = (current: PresenceAnswer, tapped: Exclude<PresenceAnswer, 'unrecorded'>): PresenceAnswer =>
	current === tapped ? 'unrecorded' : tapped;

/**
 * "Mark the rest as no agent" — the common case is 2-3 present of 20, so this turns
 * seventeen taps into one. It ONLY fills blanks: an answer the agent already gave is never
 * overwritten, which is what makes the action safe to offer at any point.
 */
export const markRemainingAbsent = (
	answers: readonly IPresenceAnswer[],
	partyIds: readonly number[]
): { next: IPresenceAnswer[]; changed: number[] } => {
	let next = [...answers];
	const changed: number[] = [];
	partyIds.forEach((partyId) => {
		if (answerOf(next, partyId) !== 'unrecorded') return;
		next = setAnswer(next, partyId, { answer: 'absent', agentName: '' });
		changed.push(partyId);
	});
	return { next, changed };
};

/**
 * Pull what the server already holds into the form, for parties this agent has NOT
 * touched. A local answer always wins — a draft restored offline must not be clobbered by
 * a read that lands afterwards.
 */
export const seedFromStored = (
	answers: readonly IPresenceAnswer[],
	stored: readonly IAgentPresenceEntry[]
): IPresenceAnswer[] => {
	let next = [...answers];
	stored.forEach((entry) => {
		if (!entry || typeof entry.partyId !== 'number' || entry.partyId <= 0) return;
		const existing = findAnswer(next, entry.partyId);
		// An answer the agent GAVE wins; a seeded default (e.g. the own-party preset, which can
		// land before this read) gives way to what is actually stored.
		if (existing && !existing.seeded) return;
		const fromStore: IPresenceAnswer = {
			partyId: entry.partyId,
			answer: entry.isPresent ? 'present' : 'absent',
			agentName: cleanText(entry.agentName),
			note: cleanText(entry.note),
			seeded: 'stored',
		};
		next = existing ? next.map((a) => (a.partyId === entry.partyId ? fromStore : a)) : [...next, fromStore];
	});
	return next;
};

/**
 * The person filling this form IS their own party's polling agent, so their own row is
 * pre-answered `present` with their name. Only when unanswered, and only when the party is
 * actually on this ballot — the agent can still change it.
 */
export const seedOwnParty = (
	answers: readonly IPresenceAnswer[],
	ownPartyId: number,
	agentName: string,
	rosterIds: readonly number[]
): IPresenceAnswer[] => {
	if (!ownPartyId || !rosterIds.includes(ownPartyId) || findAnswer(answers, ownPartyId)) return [...answers];
	return [
		...answers,
		{
			partyId: ownPartyId,
			answer: 'present',
			agentName: cleanText(agentName).slice(0, AGENT_NAME_MAX),
			note: '',
			seeded: 'preset',
		},
	];
};

export interface IPresenceTally {
	present: number;
	absent: number;
	unrecorded: number;
	/** present + absent */
	answered: number;
	total: number;
}

/** Counts over the ballot's roster, so unanswered parties count as `unrecorded`. */
export const tallyPresence = (answers: readonly IPresenceAnswer[], rosterIds: readonly number[]): IPresenceTally => {
	let present = 0;
	let absent = 0;
	rosterIds.forEach((partyId) => {
		const answer = answerOf(answers, partyId);
		if (answer === 'present') present += 1;
		else if (answer === 'absent') absent += 1;
	});
	return {
		present,
		absent,
		unrecorded: rosterIds.length - present - absent,
		answered: present + absent,
		total: rosterIds.length,
	};
};

/* ───────────────────────────── the write ───────────────────────────── */

export interface IPresencePutPlan {
	body: ISetAgentPresenceRequest;
	/** Stored rows this write removes — only ever ones the agent explicitly cleared. */
	deletes: number[];
	hasChanges: boolean;
}

/**
 * Local answers over the stored record, as a full-replace body.
 *
 *  - Every stored row goes back out VERBATIM unless the agent answered that party. An
 *    unchanged row omitted "because nothing happened to it" would be DELETED, and on this
 *    record a verifier may have answered rows the agent never looked at.
 *  - `present`/`absent` answers go out, changed or not.
 *  - `unrecorded` on a party with a stored row is an explicit clear → omitted → deleted.
 *    `unrecorded` on a party with nothing stored sends nothing (omission IS "no answer").
 *  - A SEEDED answer is a value the agent merely saw, so the fresh read wins: one copied
 *    from an earlier read is never written (not even to re-create a row a verifier has
 *    since deleted), and the own-party preset applies only where nothing is stored now.
 *  - An agent name beside "no agent" is dropped: the two would contradict each other.
 *
 * Keyed by `partyId`, so `CONTRADICTORY_DUPLICATE_PARTY` is structurally unreachable.
 * Presence is NEVER inferred from votes — a party that scored with nobody watching is the
 * finding, and seeding from votes would hide it by construction.
 */
export const buildPresencePut = ({
	electionId,
	pollingUnitCode,
	answers,
	stored,
}: {
	electionId: number;
	pollingUnitCode: string;
	answers: readonly IPresenceAnswer[];
	stored: readonly IAgentPresenceEntry[];
}): IPresencePutPlan => {
	const byParty = new Map<number, IAgentPresenceInput>();
	const storedById = new Map<number, IAgentPresenceInput>();

	stored.forEach((entry) => {
		if (!entry || typeof entry.partyId !== 'number' || entry.partyId <= 0) return;
		const input: IAgentPresenceInput = {
			partyId: entry.partyId,
			isPresent: !!entry.isPresent,
			agentName: entry.isPresent ? toWireText(entry.agentName, AGENT_NAME_MAX) : null,
			note: toWireText(entry.note, AGENT_NOTE_MAX),
		};
		storedById.set(entry.partyId, input);
		byParty.set(entry.partyId, input);
	});

	const deletes: number[] = [];
	answers.forEach((a) => {
		if (!a || a.partyId <= 0) return;
		if (a.seeded === 'stored') return;
		if (a.seeded === 'preset' && storedById.has(a.partyId)) return;
		if (a.answer === 'unrecorded') {
			if (byParty.delete(a.partyId)) deletes.push(a.partyId);
			return;
		}
		const isPresent = a.answer === 'present';
		byParty.set(a.partyId, {
			partyId: a.partyId,
			isPresent,
			agentName: isPresent ? toWireText(a.agentName, AGENT_NAME_MAX) : null,
			note: toWireText(a.note, AGENT_NOTE_MAX),
		});
	});

	const parties = [...byParty.values()].sort((x, y) => x.partyId - y.partyId);

	const same = (x: IAgentPresenceInput, y: IAgentPresenceInput | undefined) =>
		!!y && x.isPresent === y.isPresent && x.agentName === y.agentName && x.note === y.note;
	const hasChanges = deletes.length > 0 || parties.some((p) => !same(p, storedById.get(p.partyId)));

	return {
		body: { electionId, pollingUnitCode: String(pollingUnitCode ?? '').trim(), parties },
		deletes,
		hasChanges,
	};
};

/* ───────────────────────────── reading responses ───────────────────────────── */

export interface IPresenceError {
	status: number;
	/** SCREAMING_SNAKE code when the Core envelope carried one. Branch on this. */
	code?: string;
	message: string;
	/** Worth offering "Retry": a network drop, a 5xx or a concurrent edit. */
	retryable: boolean;
}

/**
 * Copy keyed on `errorCode`, never on `body.message` (server prose changes without notice).
 * The write validates fully before touching anything, so a rejection leaves the stored record
 * exactly as it was — which is what licenses "Nothing was recorded".
 */
export const PRESENCE_MESSAGES: Record<string, string> = {
	NOT_ASSIGNED_TO_POLLING_UNIT: 'You can only record polling agents for the polling unit you are assigned to.',
	INVALID_ELECTION_ID: 'No election is selected, so there is nothing to record against.',
	INVALID_POLLING_UNIT_CODE: 'This polling unit code is not one the server accepts.',
	INVALID_PARTY_ID: 'One of the parties has no id. Refresh the party list and try again.',
	PARTIES_REQUIRED: 'Nothing was sent. Try again.',
	TOO_MANY_PARTIES: 'More than 50 parties for one polling unit — more than the record supports.',
	CONTRADICTORY_DUPLICATE_PARTY: 'The same party was sent twice with different answers. Try again.',
	PARTY_NOT_FOUND: 'The party list has changed. Refresh it and try again. Nothing was recorded.',
	AGENT_NAME_TOO_LONG: 'An agent name is longer than 128 characters. Nothing was recorded.',
	NOTE_TOO_LONG: 'A note is longer than 500 characters. Nothing was recorded.',
	ELECTION_NOT_FOUND: 'That election no longer exists.',
	POLLING_UNIT_NOT_FOUND: 'No polling unit matches this code. Nothing was recorded.',
	AMBIGUOUS_POLLING_UNIT_CODE:
		'More than one polling unit carries this code, so the server will not guess which one this is. Report this to your coordinator.',
	CONCURRENT_MODIFICATION: 'Someone else updated this unit’s polling agents at the same moment. Retry to save yours on top.',
};

export const PRESENCE_FALLBACK = 'The polling agents could not be saved. Nothing was recorded.';
const OFFLINE_MESSAGE = 'No connection. Your answers are kept — retry when you are back online.';
/**
 * 403 with an EMPTY body: the gate refused before the controller ran (an older api.core).
 * Never append "your result was submitted" here — the notice that renders this already says it.
 */
const FORBIDDEN_MESSAGE = 'Recording polling agents is not enabled for your account yet.';

const RETRYABLE_CODES = new Set(['CONCURRENT_MODIFICATION']);

/**
 * Three wire shapes reach this, and only one is the Core envelope:
 *  1. `{ isSuccessful: false, errorCode, … }` — every semantic failure.
 *  2. ASP.NET `ValidationProblemDetails` from model binding — NO `errorCode` at all.
 *  3. An EMPTY body on 401/403 from the permission middleware.
 * `status === 0` means the request never got an answer.
 */
export const readPresenceError = (status: number, body: unknown): IPresenceError => {
	if (!status) return { status: 0, message: OFFLINE_MESSAGE, retryable: true };

	const envelope =
		body && typeof body === 'object' ? (body as { isSuccessful?: unknown; errorCode?: unknown; title?: unknown }) : undefined;
	const code = envelope && typeof envelope.errorCode === 'string' ? envelope.errorCode : undefined;

	if (code) {
		return { status, code, message: PRESENCE_MESSAGES[code] || PRESENCE_FALLBACK, retryable: RETRYABLE_CODES.has(code) };
	}
	if (status === 401) return { status, message: 'Your session has expired. Sign in again, then retry.', retryable: true };
	if (status === 403) return { status, message: FORBIDDEN_MESSAGE, retryable: false };
	if (envelope && 'title' in envelope) {
		return { status, message: 'The server could not read one of the fields sent. Nothing was recorded.', retryable: false };
	}
	return { status, message: PRESENCE_FALLBACK, retryable: status >= 500 || status === 408 || status === 429 };
};

export type PresenceRead =
	| { kind: 'stored'; parties: IAgentPresenceEntry[] }
	/** A real unit with nothing recorded yet — the normal first state, not an error. */
	| { kind: 'none' }
	| { kind: 'error'; error: IPresenceError };

/**
 * `GET by-polling-unit`, classified. 404 `COVERAGE_NOT_FOUND` is the most common response the
 * endpoint will ever give, so it maps to `none`. `POLLING_UNIT_NOT_FOUND`/`ELECTION_NOT_FOUND`
 * are real errors: writing against them would fail anyway.
 */
export const toPresenceRead = (status: number, body: unknown): PresenceRead => {
	const envelope =
		body && typeof body === 'object' ? (body as { isSuccessful?: unknown; errorCode?: unknown; data?: unknown }) : undefined;
	if (status >= 200 && status < 300 && envelope?.isSuccessful !== false) {
		const data = envelope?.data as IAgentPresenceRoster | undefined;
		return { kind: 'stored', parties: Array.isArray(data?.parties) ? data.parties : [] };
	}
	if (status === 404 && envelope?.errorCode === 'COVERAGE_NOT_FOUND') return { kind: 'none' };
	return { kind: 'error', error: readPresenceError(status, body) };
};
